import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { clientApi } from "@/lib/api";
import { ClientLayout } from "@/components/layout/ClientLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, ChevronLeft, ChevronRight, Landmark } from "lucide-react";
import { formatCurrency } from "@/lib/format";

export default function FundExplorer() {
  const [search, setSearch] = useState("");
  const [amc, setAmc] = useState("");
  const [schemeType, setSchemeType] = useState("");
  const [sipOnly, setSipOnly] = useState(false);
  const [amcs, setAmcs] = useState([]);
  const [types, setTypes] = useState([]);
  const [data, setData] = useState({ items: [], total: 0, total_pages: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    clientApi.get("/schemes/amcs").then((r) => setAmcs(r.data));
    clientApi.get("/schemes/types").then((r) => setTypes(r.data));
  }, []);

  const fetchSchemes = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 12 };
      if (search) params.search = search;
      if (amc) params.amc_code = amc;
      if (schemeType) params.scheme_type = schemeType;
      if (sipOnly) params.sip_flag = true;
      const { data } = await clientApi.get("/schemes", { params });
      setData(data);
    } finally {
      setLoading(false);
    }
  }, [search, amc, schemeType, sipOnly, page]);

  useEffect(() => {
    const t = setTimeout(fetchSchemes, 300);
    return () => clearTimeout(t);
  }, [fetchSchemes]);

  useEffect(() => setPage(1), [search, amc, schemeType, sipOnly]);

  return (
    <ClientLayout>
      <div className="mb-6">
        <h1 className="font-heading text-3xl text-[var(--brand)]">Explore Funds</h1>
        <p className="mt-1 text-sm text-[var(--ink-muted)]">Regular plans only &middot; {data.total.toLocaleString("en-IN")} schemes across {amcs.length} AMCs</p>
      </div>

      <Card className="mb-6 border-[var(--glass-border)]">
        <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--ink-muted)]" />
            <Input placeholder="Search by fund or AMC name..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="fund-search-input" />
          </div>
          <Select value={amc} onValueChange={setAmc}>
            <SelectTrigger className="w-full sm:w-[200px]" data-testid="fund-filter-amc-select"><SelectValue placeholder="All AMCs" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="__all">All AMCs</SelectItem>
              {amcs.map((a) => <SelectItem key={a.amc_code} value={a.amc_code}>{a.amc_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={schemeType} onValueChange={setSchemeType}>
            <SelectTrigger className="w-full sm:w-[160px]" data-testid="fund-filter-category-select"><SelectValue placeholder="All Categories" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="__all">All Categories</SelectItem>
              {types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button
            variant={sipOnly ? "default" : "outline"}
            className={sipOnly ? "rounded-full bg-[var(--cta)]" : "rounded-full"}
            onClick={() => setSipOnly((v) => !v)}
            data-testid="fund-filter-sip-toggle"
          >
            SIP eligible
          </Button>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="fund-results-table">
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-40 rounded-2xl" />)
        ) : data.items.length === 0 ? (
          <div className="col-span-full py-16 text-center text-[var(--ink-muted)]">No schemes match your filters.</div>
        ) : (
          data.items.map((s) => (
            <Link
              key={s.scheme_code}
              to={`/funds/${s.scheme_code}`}
              data-testid={`fund-card-${s.scheme_code}`}
              className="block"
            >
            <Card
              className="h-full cursor-pointer border-[var(--glass-border)] transition-shadow hover:shadow-md"
            >
              <CardContent className="flex h-full flex-col gap-3 p-5">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[var(--surface-2)]">
                    <Landmark className="h-4 w-4 text-[var(--brand)]" />
                  </div>
                  <Badge variant="outline" className="shrink-0 rounded-full text-[10px]">{s.scheme_type}</Badge>
                </div>
                <div>
                  <div className="line-clamp-2 text-sm font-semibold text-[var(--ink)]">{s.base_scheme_name || s.scheme_name}</div>
                  <div className="mt-1 text-xs text-[var(--ink-muted)]">{s.amc_name} &middot; Regular &middot; {s.plan_variant}</div>
                </div>
                <div className="mt-auto flex items-center justify-between text-xs">
                  <span className="text-[var(--ink-muted)]">Min. investment</span>
                  <span className="font-semibold text-[var(--brand)]">{s.transaction_metadata_verified ? formatCurrency(s.min_purchase_amt) : "BSE confirms"}</span>
                </div>
                <div className="flex gap-1.5">
                  {s.sip_flag === true && <Badge className="rounded-full bg-[var(--surface-2)] text-[10px] text-[var(--brand)]">SIP</Badge>}
                  {s.switch_flag === true && <Badge className="rounded-full bg-[var(--surface-2)] text-[10px] text-[var(--brand)]">Switch</Badge>}
                </div>
              </CardContent>
            </Card>
            </Link>
          ))
        )}
      </div>

      {data.total_pages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-3">
          <Button variant="outline" size="icon" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="fund-prev-page-button">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-[var(--ink-muted)]">Page {page} of {data.total_pages}</span>
          <Button variant="outline" size="icon" disabled={page >= data.total_pages} onClick={() => setPage((p) => p + 1)} data-testid="fund-next-page-button">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </ClientLayout>
  );
}
